import { Article } from 'components/article/types'
import { routes } from 'utils/routes'
import dayjs from 'dayjs'
import { Language } from 'types/language'
import { CmsBlogArticle, CmsBlogExternalArticle, CmsEncyclopediaArticle, CmsFaqArticle, CmsFaqCategory } from 'types/cms/collection-types'
import { CmsArticleTypes } from 'types/cms/enums'
import { FaqSection } from '@boltenergy-be/design-system'
import parse from 'html-react-parser'
import StyledBlocksRenderer from 'components/styled-blocks-renderer/StyledBlocksRenderer'
import { AccordionItem } from '@boltenergy-be/design-system'

/**
 * Helper function that sorts & maps an array of CmsBlogArticle objects to an array of Article objects
 *
 * @param {CmsBlogArticle[]} cmsBlogArticles
 * @param {number} [limit]
 * @returns {Article[]}
 */
export const mapAndSortCmsBlogArticlesToArticles = (cmsBlogArticles: CmsBlogArticle[], limit: number = 4): Article[] => {
  const articles = mapCmsBlogArticlesToArticles(cmsBlogArticles)
  return sortArticlesOnPublishedDate(articles, limit)
}

/**
 * Helper function that maps a CmsBlogArticle object to an Article object
 *
 * @param {CmsBlogArticle} cmsBlogArticle
 * @returns {Article}
 */
export const mapCmsBlogArticleToArticle = (cmsBlogArticle: CmsBlogArticle): Article => {
  return {
    author: {
      avatarPublicId: cmsBlogArticle.author?.avatar?.provider_metadata.public_id ?? null,
      avatarUrl: cmsBlogArticle.author?.avatar?.url ?? null,
      firstName: cmsBlogArticle.author?.firstName,
      personalizedFooter: cmsBlogArticle.author?.personalizedFooter ?? null
    },
    ...(cmsBlogArticle.category && {
      category: {
        slug: cmsBlogArticle.category.slug,
        title: cmsBlogArticle.category.title
      }
    }),
    contentBlocks: cmsBlogArticle.contentBlocks ?? null,
    description: cmsBlogArticle.description,
    destination: routes(cmsBlogArticle.locale, cmsBlogArticle.slug).blogArticle,
    id: cmsBlogArticle.id,
    locale: cmsBlogArticle.locale as Language,
    publishedAt: cmsBlogArticle.customPublishedAt ?? cmsBlogArticle.publishedAt,
    slug: cmsBlogArticle.slug,
    thumbnail: cmsBlogArticle.thumbnail,
    title: cmsBlogArticle.title,
    type: CmsArticleTypes.BLOG_INTERNAL
  }
}

/**
 * Helper function that maps a CmsBlogArticle object to an Article object
 *
 * @param {CmsBlogArticle[]} cmsBlogArticles
 * @returns {Article[]}
 */
export const mapCmsBlogArticlesToArticles = (cmsBlogArticles: CmsBlogArticle[]): Article[] => {
  return cmsBlogArticles.map((article) => mapCmsBlogArticleToArticle(article))
}

/**
 * Helper function that maps a CmsBlogExternalArticle object to an Article object
 *
 * @param {CmsBlogExternalArticle} cmsBlogExternalArticle
 * @returns {Article}
 */
export const mapCmsBlogExternalArticleToArticle = (cmsBlogExternalArticle: CmsBlogExternalArticle): Article => {
  return {
    author: {
      firstName: cmsBlogExternalArticle?.mediaInstance
    },
    ...(cmsBlogExternalArticle.category && {
      category: {
        slug: cmsBlogExternalArticle.category.slug,
        title: cmsBlogExternalArticle.category.title
      }
    }),
    destination: cmsBlogExternalArticle.url,
    id: cmsBlogExternalArticle.id,
    locale: cmsBlogExternalArticle.locale as Language,
    publishedAt: cmsBlogExternalArticle.customPublishedAt ?? cmsBlogExternalArticle.publishedAt,
    slug: cmsBlogExternalArticle.url,
    thumbnail: cmsBlogExternalArticle.thumbnail,
    title: cmsBlogExternalArticle.title,
    type: CmsArticleTypes.BLOG_EXTERNAL
  }
}

/**
 * Helper function that maps a CmsBlogExternalArticle object to an Article object
 *
 * @param {CmsBlogExternalArticle[]} cmsBlogExternalArticles
 * @returns {Article[]}
 */
export const mapCmsBlogExternalArticlesToArticles = (cmsBlogExternalArticles: CmsBlogExternalArticle[]): Article[] => {
  return cmsBlogExternalArticles.map((article) => mapCmsBlogExternalArticleToArticle(article))
}

/**
 * Helper function that maps a CmsEncyclopediaArticle object to an Article object
 *
 * @param {CmsEncyclopediaArticle} cmsEncyclopediaArticle
 * @returns {Article}
 */
export const mapCmsEncyclopediaArticleToArticle = (cmsEncyclopediaArticle: CmsEncyclopediaArticle): Article => {
  const {
    author,
    customPublishedAt,
    description,
    isHighlighted,
    isHighlightedLanding,
    isPopular,
    locale,
    publishedAt,
    slug,
    thumbnail,
    category,
    title
  } = cmsEncyclopediaArticle

  return {
    author: {
      avatarPublicId: author?.avatar?.provider_metadata.public_id ?? null,
      avatarUrl: author?.avatar?.url ?? null,
      firstName: author?.firstName,
      personalizedFooter: author?.personalizedFooter ?? null
    },
    category: {
      color: category.color,
      slug: category.slug,
      title: category.title
    },
    contentBlocks: cmsEncyclopediaArticle.contentBlocks ?? null,
    description,
    destination: routes(locale, undefined, [category.slug, slug]).encyclopediaArticle,
    id: cmsEncyclopediaArticle.id,
    isHighlighted,
    isHighlightedLanding,
    isPopular,
    locale: locale as Language,
    publishedAt: customPublishedAt ?? publishedAt,
    slug,
    thumbnail,
    title,
    type: CmsArticleTypes.ENCYCLOPEDIA
  }
}

/**
 * Helper function that maps a CmsEncyclopediaArticle object to an Article object
 *
 * @param {CmsEncyclopediaArticle[]} cmsEncyclopediaArticles
 * @returns {Article[]}
 */
export const mapCmsEncyclopediaArticlesToArticles = (cmsEncyclopediaArticles: CmsEncyclopediaArticle[]): Article[] => {
  return cmsEncyclopediaArticles.map((article) => mapCmsEncyclopediaArticleToArticle(article))
}

/**
 * Helper function that maps a CmsFaqArticle object to an AccordionItem object
 * @param {CmsFaqArticle} article
 * @returns {AccordionItem}
 */
export const mapCmsFaqArticleToAccordionItem = (article: CmsFaqArticle): AccordionItem => ({
  title: article.question,
  anchor: article.anchor,
  children: article.content ? <StyledBlocksRenderer content={article.content} /> : parse(article.answer)
})

/**
 * Helper function that maps a CmsFaqCategory object to a FaqSection object
 *
 * @param {CmsFaqCategory} category
 * @returns {FaqSection}
 */
export const mapFaqCategoryToFaqSection = (category: CmsFaqCategory): FaqSection => {
  return {
    title: category.name,
    slug: category.slug,
    items: category.articles.map(mapCmsFaqArticleToAccordionItem)
  }
}

/**
 * Sorts articles on published date
 *
 * @param {Article[]} articles
 * @param {number} [limit]
 * @returns {Article[]}
 */
export const sortArticlesOnPublishedDate = (articles: Article[], limit?: number): Article[] => {
  const sortedArticles = [...articles].sort((a, b) => {
    const dateA = dayjs(a.publishedAt)
    const dateB = dayjs(b.publishedAt)

    return dateB.isBefore(dateA) ? -1 : 1
  })

  return limit ? sortedArticles.slice(0, limit) : sortedArticles
}

/**
 * Sorts CMS Blog Articles on published date
 *
 * @param {CmsBlogArticle[]} cmsBlogArticles
 * @param {number} [limit]
 * @returns {Article[]}
 */
export const sortCmsBlogArticlesOnPublishedDate = (cmsBlogArticles: CmsBlogArticle[], limit?: number): CmsBlogArticle[] => {
  const sortedArticles = [...cmsBlogArticles].sort((a, b) => {
    const dateA = dayjs(a.customPublishedAt ?? a.publishedAt)
    const dateB = dayjs(b.customPublishedAt ?? b.publishedAt)

    return dateB.isBefore(dateA) ? -1 : 1
  })

  return limit ? sortedArticles.slice(0, limit) : sortedArticles
}
